import {
  ASCENSION_SPIRIT_POWER,
  getNextLevelGrowthConfig,
  normalizeGameplayLevel,
  SPIRIT_STONE_CLASS_CONFIGS,
} from "@xiuxian/game-core";
import {
  formatAscensionTarget,
  formatRealmLayer,
} from "./i18n/game-formatters.js";
import { DEFAULT_LOCALE, type Locale } from "./i18n/index.js";

export interface SpiritProgress {
  label: string;
  current: number;
  next: number | null;
}

export interface SpiritStoneVisualStyle {
  shape: "shard" | "diamond" | "octagon" | "crystal" | "cluster" | "marrow";
  color: number;
  alpha: number;
  radius: number;
  coreColor: number;
  coreAlpha: number;
  /** Thick dark-ink outline for the Q 版 chunky-gem look. */
  outlineColor: number;
  outlineWidth: number;
  outlineAlpha: number;
  ringColor: number;
  ringAlpha: number;
  ringWidth: number;
  highlightColor: number;
  highlightAlpha: number;
  idlePulseStrength: number;
  auraAlpha: number;
  auraRadiusOffset: number;
  sparkleCount: number;
  sparkleAlpha: number;
}

export function getSpiritProgress(
  spiritPower: number,
  level: number,
  locale: Locale = DEFAULT_LOCALE,
): SpiritProgress {
  const normalizedLevel = normalizeGameplayLevel(level);
  const current = Math.max(0, Math.floor(spiritPower));
  const growthLabel = formatRealmLayer(locale, normalizedLevel);
  const nextGrowth = getNextLevelGrowthConfig(normalizedLevel);

  if (!nextGrowth) {
    if (current < ASCENSION_SPIRIT_POWER) {
      return {
        label: `${growthLabel} · ${current.toString()} / ${ASCENSION_SPIRIT_POWER.toString()}`,
        current,
        next: ASCENSION_SPIRIT_POWER,
      };
    }
    return {
      label: `${growthLabel} · ${current.toString()} / ${formatAscensionTarget(locale)}`,
      current,
      next: null,
    };
  }

  return {
    label: `${growthLabel} · ${current.toString()} / ${nextGrowth.requiredSpiritPower.toString()}`,
    current,
    next: nextGrowth.requiredSpiritPower,
  };
}

export function getSpiritStoneVisualStyle(
  value: number,
): SpiritStoneVisualStyle {
  // 高级灵石 (advanced, condensed): a large premium blue gem with a bright core
  // and a subtle jewel facet — clearly the valuable one.
  if (value >= SPIRIT_STONE_CLASS_CONFIGS.condensed.value) {
    return {
      shape: "octagon",
      color: 0x5aa8ff,
      alpha: 0.98,
      radius: 10.5,
      coreColor: 0xeafaff,
      coreAlpha: 0.62,
      outlineColor: 0x0e2742,
      outlineWidth: 2.6,
      outlineAlpha: 0.94,
      ringColor: 0xd6efff,
      ringAlpha: 0.34,
      ringWidth: 1.4,
      highlightColor: 0xffffff,
      highlightAlpha: 0.46,
      idlePulseStrength: 0.04,
      auraAlpha: 0.1,
      auraRadiusOffset: 8,
      sparkleCount: 1,
      sparkleAlpha: 0.22,
    };
  }
  // 普通灵石 (common, fragment): a solid chunky green gem — no longer a tiny
  // low-presence shard; big enough to read clearly on the ground.
  return {
    shape: "diamond",
    color: 0x36c98a,
    alpha: 0.98,
    radius: 7,
    coreColor: 0xd8ffe7,
    coreAlpha: 0.44,
    outlineColor: 0x0f2a1f,
    outlineWidth: 2.2,
    outlineAlpha: 0.92,
    ringColor: 0xb3ffd9,
    ringAlpha: 0,
    ringWidth: 0,
    highlightColor: 0xd8ffe7,
    highlightAlpha: 0.22,
    idlePulseStrength: 0.02,
    auraAlpha: 0,
    auraRadiusOffset: 0,
    sparkleCount: 0,
    sparkleAlpha: 0,
  };
}
